import { useBrandUrlNames } from "@/lib/brand-url-routing";
import {
  LayoutDashboard, Users, Package, MessageSquare, Crosshair,
  FileText, FileSpreadsheet, Sparkles, BookOpen, LogOut, Building2,
  Layers, ImageIcon, ImagePlus, LayoutGrid, Lightbulb, Shield, Settings, Pencil,
  ChevronsUpDown, ChevronRight, Plus, Check, Loader2, Eye, Video, Clapperboard, GraduationCap,
  User, UserCircle, Lock, Coins, Receipt, LayoutTemplate, Megaphone, Zap, Film, Shirt, Tag, Percent, Rocket, Brain, Mail, Shapes, Images, Laugh, TrendingUp, PlaneTakeoff, Compass, HeartPulse, CalendarCheck, Pin, PinOff, Target, TerminalSquare, BellRing,
} from "lucide-react";
import { z } from "zod";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuAction,
  SidebarHeader, SidebarFooter, SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Brand, Workspace } from "@shared/schema";
import { LP_EXPRESS_2_BETA_FLAG, type BrandNavItemKey } from "@shared/schema";
import { optimizedImg } from "@/lib/img";
import { ImgWithFallback } from "@/components/img-with-fallback";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { canShowTemplateStudioTypes, isConceptBoardsDestination, withoutRetiredConceptBoardPins } from "@/lib/template-studio-navigation";
import {
  hasCompleteAdLauncherSetup,
  shouldShowAdsManager,
  type BrandAdNavigationSettings,
} from "@/lib/brand-ad-navigation";

// Master-only brand grouping (shared with the dashboard grid). The server
// strips clientCategory from brand payloads for non-masters, so grouping only
// ever applies to masters; group order is fixed, empty groups are hidden.
import { groupBrandsByClientCategory } from "@/lib/brand-client-categories";
import { useCreativeStrategistOpen } from "@/components/creative-strategist-guard";
import { useWeeklyDeliverablesEnabled } from "@/pages/deliverables";
import { useCustomerEvidenceAccess } from "@/hooks/use-customer-evidence-access";

// Collapsible client-category group inside the brand-switcher dropdown.
// All groups start collapsed; the dropdown unmounts on close, so open state
// resets on each open.
function BrandCategoryGroup({
  group,
  renderBrandItem,
}: {
  group: { key: string; label: string; items: Brand[] };
  renderBrandItem: (brand: Brand) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`brand-group-${group.key}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold hover:text-foreground transition-colors cursor-pointer"
        data-testid={`brand-group-toggle-${group.key}`}
      >
        <span>{group.label} ({group.items.length})</span>
        <ChevronRight
          className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && group.items.map((brand) => renderBrandItem(brand))}
    </div>
  );
}

function CreditBalance({ brandId }: { brandId?: string }) {
  // Pass the active brand so the server can hide the widget inside trial
  // brands (decision stays server-side; the client only learns the boolean).
  const { data } = useQuery<{ credits: number; balance: number; multiplier: number; hideCreditsUi?: boolean }>({
    queryKey: [`/api/me/credits${brandId ? `?brandId=${brandId}` : ""}`],
    refetchInterval: 10_000,
  });

  if (!data) return null;
  // Server says hide (trial-only accounts get no misleading 0/0 widget).
  if (data.hideCreditsUi) return null;

  const credits = data.credits ?? 0;
  const balance = data.balance ?? 0;
  const pct = balance > 0 ? Math.round((credits / balance) * 100) : 0;

  return (
    <div
      className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/15 px-3 py-2.5 space-y-2"
      data-testid="sidebar-credit-balance"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Coins className="h-4 w-4 text-primary" />
          <span className="text-xs font-medium text-foreground">Credits</span>
        </div>
        <span className="text-sm font-bold tabular-nums text-foreground" data-testid="text-credit-balance">
          {credits.toLocaleString()}<span className="text-muted-foreground font-normal text-xs">/{balance.toLocaleString()}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-primary/10 overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface AppSidebarProps {
  brandId?: string;
  brandName?: string;
}

type BrandLeaf = { key: BrandNavItemKey; title: string; url: string; icon: any };
type BrandSubgroup = { title: string; icon: any; children: BrandLeaf[] };
type BrandNavEntry = BrandLeaf | BrandSubgroup;
type BrandToolSection = { title: string; items: BrandNavEntry[] };

function isBrandSubgroup(item: BrandNavEntry): item is BrandSubgroup {
  return "children" in item;
}

function hasActiveBrandEntry(item: BrandNavEntry, location: string): boolean {
  return isBrandSubgroup(item)
    ? item.children.some((child) => hasActiveBrandEntry(child, location))
    : item.key === "concept-boards"
      ? isConceptBoardsDestination(location, item.url)
      : location === item.url;
}

function PinButton({
  item,
  pinned,
  pending,
  testId,
  onToggle,
}: {
  item: BrandLeaf;
  pinned: boolean;
  pending: boolean;
  testId: string;
  onToggle: (item: BrandLeaf) => void;
}) {
  return (
    <SidebarMenuAction
      type="button"
      showOnHover
      disabled={pending}
      aria-label={pinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
      aria-pressed={pinned}
      title={pinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
      data-testid={testId}
      className="text-muted-foreground/60 opacity-0 transition-opacity group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100 focus-visible:opacity-100 hover:text-foreground"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle(item);
      }}
    >
      {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
    </SidebarMenuAction>
  );
}

function BrandLeafItem({
  item,
  location,
  pinned,
  pending,
  pinnedCopy = false,
  testId,
  onTogglePin,
}: {
  item: BrandLeaf;
  location: string;
  pinned: boolean;
  pending: boolean;
  pinnedCopy?: boolean;
  testId?: string;
  onTogglePin: (item: BrandLeaf) => void;
}) {
  return (
    <SidebarMenuItem data-testid={testId}>
      <SidebarMenuButton asChild data-active={hasActiveBrandEntry(item, location)}>
        <Link
          href={item.url}
          data-testid={pinnedCopy
            ? `link-pinned-${item.key}`
            : `link-brand-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <item.icon className="h-4 w-4" />
          <span className="text-[13px]">{item.title}</span>
        </Link>
      </SidebarMenuButton>
      <PinButton
        item={item}
        pinned={pinned}
        pending={pending}
        testId={pinnedCopy ? `button-pin-pinned-${item.key}` : `button-pin-${item.key}`}
        onToggle={onTogglePin}
      />
    </SidebarMenuItem>
  );
}

function BrandGroupItem({
  group,
  location,
  manuallyOpen,
  onManualOpenChange,
  pinnedKeys,
  pendingKeys,
  onTogglePin,
}: {
  group: BrandSubgroup;
  location: string;
  manuallyOpen: boolean;
  onManualOpenChange: (open: boolean) => void;
  pinnedKeys: Set<BrandNavItemKey>;
  pendingKeys: Set<BrandNavItemKey>;
  onTogglePin: (item: BrandLeaf) => void;
}) {
  const hasActive = hasActiveBrandEntry(group, location);
  const open = manuallyOpen || hasActive;
  const Icon = group.icon;
  return (
    <Collapsible open={open} onOpenChange={onManualOpenChange} asChild>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            data-active={hasActive}
            data-testid={`group-${group.title.toLowerCase().replace(/\s+/g, "-")}`}
            className="cursor-pointer"
          >
            <Icon className="h-4 w-4" />
            <span className="text-[13px] flex-1">{group.title}</span>
            <ChevronRight
              className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenu className="ml-4 mt-0.5 border-l border-border/50 pl-2">
            {group.children.map((child) => (
              <BrandLeafItem
                key={child.key}
                item={child}
                location={location}
                pinned={pinnedKeys.has(child.key)}
                pending={pendingKeys.has(child.key)}
                onTogglePin={onTogglePin}
              />
            ))}
          </SidebarMenu>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function BrandToolSection({
  section,
  location,
  manuallyOpen,
  manuallyOpenGroups,
  onManualOpenChange,
  onGroupManualOpenChange,
  pinnedKeys,
  pendingKeys,
  onTogglePin,
}: {
  section: BrandToolSection;
  location: string;
  manuallyOpen: boolean;
  manuallyOpenGroups: ReadonlySet<string>;
  onManualOpenChange: (open: boolean) => void;
  onGroupManualOpenChange: (groupTitle: string, open: boolean) => void;
  pinnedKeys: Set<BrandNavItemKey>;
  pendingKeys: Set<BrandNavItemKey>;
  onTogglePin: (item: BrandLeaf) => void;
}) {
  const hasActive = section.items.some((item) => hasActiveBrandEntry(item, location));
  const open = manuallyOpen || hasActive;
  return (
    <Collapsible open={open} onOpenChange={onManualOpenChange}>
      <SidebarGroup className="pt-1">
        <CollapsibleTrigger className="flex h-8 w-full items-center justify-between rounded-md px-2 text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors">
          <span>{section.title}</span>
          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              {section.items.map((item) =>
                isBrandSubgroup(item) ? (
                  <BrandGroupItem
                    key={item.title}
                    group={item}
                    location={location}
                   manuallyOpen={manuallyOpenGroups.has(item.title)}
                   onManualOpenChange={(nextOpen) => onGroupManualOpenChange(item.title, nextOpen)}
                    pinnedKeys={pinnedKeys}
                    pendingKeys={pendingKeys}
                    onTogglePin={onTogglePin}
                  />
                ) : (
                  <BrandLeafItem
                    key={item.key}
                    item={item}
                    location={location}
                    pinned={pinnedKeys.has(item.key)}
                    pending={pendingKeys.has(item.key)}
                    onTogglePin={onTogglePin}
                  />
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

export function AppSidebar({ brandId, brandName }: AppSidebarProps) {
  const { user } = useAuth();
  const customerEvidenceAccess = useCustomerEvidenceAccess(brandId);
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [manuallyOpenSections, setManuallyOpenSections] = useState<Set<string>>(new Set());
  const [manuallyOpenGroups, setManuallyOpenGroups] = useState<Set<string>>(new Set());

  const updateManualOpen = (
    setter: typeof setManuallyOpenSections,
    key: string,
    open: boolean,
  ) => {
    setter((current) => {
      const next = new Set(current);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const { data: workspaces } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
    enabled: !!user,
  });
  const workspaceId = workspaces?.[0]?.id;

  const { data: workspaceBrands, isLoading: brandsLoading } = useQuery<Brand[]>({
    queryKey: [`/api/brands?workspaceId=${workspaceId}`],
    enabled: !!workspaceId,
  });

  const internalUrls = useBrandUrlNames();
  const brands = internalUrls.enabled ? internalUrls.brands as Brand[] : workspaceBrands;
  const activeBrand = brandId ? brands?.find((b) => b.id === brandId) : undefined;
  const activeBrandLogo = activeBrand?.logoUrl || undefined;
  const { data: performanceAvailability } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/ads-performance/availability", user?.id],
    queryFn: async () => {
      const response = await fetch("/api/ads-performance/availability", { credentials: "include" });
      if (!response.ok) throw new Error("Performance Brief availability unavailable");
      return z.object({ enabled: z.boolean() }).parse(await response.json());
    },
    enabled: !!user,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const {
    data: smbAdminAccess,
    isFetching: smbAdminAccessFetching,
    isFetchedAfterMount: smbAdminAccessVerified,
    isError: smbAdminAccessError,
  } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/admin/smb/access", user?.id],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/smb/access");
      return response.json();
    },
    enabled: !!user?.isMaster,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const mainItems = [
    { title: "Dashboard", url: "/", icon: LayoutDashboard },
    ...(user?.isMaster ? [{ title: "Brands dashboard", url: "/brands-dashboard", icon: MessageSquare }] : []),
    ...(user?.isMaster ? [{ title: "Campaign alerts", url: "/admin/campaign-alerts", icon: BellRing }] : []),
    ...(performanceAvailability?.enabled === true
      ? [{ title: "All Brands Performance", url: "/ads-performance", icon: Target }]
      : []),
  ];

  const brandSettings = (activeBrand?.settings || {}) as Record<string, boolean> & BrandAdNavigationSettings;
  const completeAdLauncherSetup = hasCompleteAdLauncherSetup(brandSettings);
  const { data: betaFeatures } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: !!brandId && !!user,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { data: boardAccess, isFetchedAfterMount: boardAccessVerified, isError: boardAccessError } = useQuery<{ enabled: boolean }>({
    queryKey: [`/api/brands/${brandId}/concept-boards/access`, user?.id],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/concept-boards/access`, { credentials: "include" });
      if (!response.ok) throw new Error("Concept Boards unavailable");
      return response.json();
    },
    enabled: !!brandId && user?.isMaster === true,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const canSeeConceptBoardTypes = boardAccessVerified && !boardAccessError &&
    canShowTemplateStudioTypes(user?.isMaster, boardAccess);
  // Hidden until the server confirms the feature is open (brand flag + global
  // switch), so a closed feature never leaves a link behind.
  const { isOpen: creativeStrategistOpen } = useCreativeStrategistOpen(
    brandId,
    !!brandSettings.enableCreativeStrategist,
  );
  // Weekly Deliverables is default-on: the effective-flags surface returns
  // false only for a brand opt-out, and navigation stays hidden until resolved.
  const { enabled: deliverablesEnabled } = useWeeklyDeliverablesEnabled(brandId);
  const {
    data: videoReferenceAvailability,
    isFetching: videoReferenceAvailabilityFetching,
    isError: videoReferenceAvailabilityError,
  } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/brands", brandId, "video-reference-ads", "availability", user?.id],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/video-reference-ads/availability`);
      if (!response.ok) throw new Error("Unavailable");
      return response.json();
    },
    enabled: !!brandId && user?.isMaster === true,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  const navPinsQueryKey = ["/api/me/nav-pins", user?.id] as const;
  const { data: navPinsResponse } = useQuery<{ keys: BrandNavItemKey[] }>({
    queryKey: navPinsQueryKey,
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/me/nav-pins");
      return parseJsonResponse<{ keys: BrandNavItemKey[] }>(response);
    },
  });
  const [pinnedKeys, setPinnedKeys] = useState<BrandNavItemKey[]>([]);
  const [pendingPinKeys, setPendingPinKeys] = useState<Set<BrandNavItemKey>>(new Set());
  const currentUserIdRef = useRef(user?.id);
  useEffect(() => {
    currentUserIdRef.current = user?.id;
    setPinnedKeys(user ? withoutRetiredConceptBoardPins(navPinsResponse?.keys ?? []) : []);
    setPendingPinKeys(new Set());
  }, [user?.id, navPinsResponse]);

  const togglePin = async (item: BrandLeaf) => {
    if (pendingPinKeys.has(item.key)) return;
    const mutationUserId = user?.id;
    if (!mutationUserId) return;
    const mutationQueryKey = ["/api/me/nav-pins", mutationUserId] as const;
    const previous = pinnedKeys;
    const pinned = !previous.includes(item.key);
    const optimistic = pinned
      ? [...previous, item.key]
      : previous.filter((key) => key !== item.key);
    setPinnedKeys(optimistic);
    setPendingPinKeys((current) => new Set(current).add(item.key));
    try {
      const response = await apiRequest(
        "PUT",
        `/api/me/nav-pins/${encodeURIComponent(item.key)}`,
        { pinned },
      );
      const saved = await parseJsonResponse<{ keys: BrandNavItemKey[] }>(response);
      queryClient.setQueryData(mutationQueryKey, saved);
      if (currentUserIdRef.current === mutationUserId) {
        setPinnedKeys(withoutRetiredConceptBoardPins(saved.keys));
      }
    } catch {
      queryClient.setQueryData(mutationQueryKey, { keys: previous });
      queryClient.invalidateQueries({ queryKey: mutationQueryKey });
      if (currentUserIdRef.current === mutationUserId) {
        setPinnedKeys(previous);
      }
    } finally {
      if (currentUserIdRef.current === mutationUserId) {
        setPendingPinKeys((current) => {
          const next = new Set(current);
          next.delete(item.key);
          return next;
        });
      }
    }
  };

  type BrandPage = { title: string; url: string; icon: any };
  const brandPagesBeforeTools: BrandPage[] = brandId
    ? [
        { title: "Overview", url: `/brands/${brandId}`, icon: LayoutDashboard },
        ...(brandSettings.enableMetaAds === true
          ? [{ title: "Performance Brief", url: `/brands/${brandId}/ads-performance`, icon: Target }]
          : []),
      ]
    : [];
  const brandPagesAfterTools: BrandPage[] = brandId
    ? [{ title: "Edit Brand", url: `/brands/${brandId}/settings`, icon: Pencil }]
    : [];

  const imageTools: BrandNavEntry[] = brandId
    ? [
        { key: "ads-express", title: "Ads Express", url: `/brands/${brandId}/ads-express`, icon: Zap },
        ...(/kill\s*crew/i.test(activeBrand?.name ?? "") || /kill\s*crew/i.test(activeBrand?.slug ?? "")
          ? [{ key: "killcrew-ad-studio", title: "KillCrew Ad Studio", url: `/brands/${brandId}/ads-express-kc`, icon: Shirt } as BrandLeaf]
          : []),
        { key: "meme-generator", title: "Meme Generator", url: `/brands/${brandId}/meme-generator`, icon: Laugh },
        { key: "ad-animator", title: "Ad Animator", url: `/brands/${brandId}/ad-animator`, icon: Clapperboard },
        ...(user?.isMaster
          ? [{ key: "slideshow-express", title: "Slideshow Express", url: `/brands/${brandId}/slideshow-express`, icon: Images } as BrandLeaf]
          : []),
      ]
    : [];

  const videoTools: BrandNavEntry[] = brandId
    ? [
        { key: "grwmugc-pb", title: "GRWMUGC PB", url: `/brands/${brandId}/grwmugc-pb`, icon: UserCircle },
        ...(user?.isMaster &&
        videoReferenceAvailability?.enabled &&
        !videoReferenceAvailabilityFetching &&
        !videoReferenceAvailabilityError
          ? [{ key: "video-reference-ads", title: "Video Reference Ads", url: `/brands/${brandId}/video-reference-ads`, icon: Film } as BrandLeaf]
          : []),
        ...(user?.isMaster || brandSettings.enableUgcExpress
          ? [{ key: "ugc-express", title: "UGC Express", url: `/brands/${brandId}/ugc-express`, icon: Video } as BrandLeaf]
          : []),
        ...(user?.isMaster || brandSettings.enableBrollExpress
          ? [{ key: "broll-express", title: "B-roll Express", url: `/brands/${brandId}/broll-express`, icon: Clapperboard } as BrandLeaf]
          : []),
        ...(user?.isMaster || brandSettings.enableVideoExpressMax
          ? [{ key: "video-express-max", title: "Video Express Max", url: `/brands/${brandId}/video-express-campaigns`, icon: Rocket } as BrandLeaf]
          : []),
      ]
    : [];

  const adTools: BrandNavEntry[] = brandId
    ? [
        { key: "my-ads", title: "My Ads", url: `/brands/${brandId}/library`, icon: BookOpen },
        ...(deliverablesEnabled
          ? [{
              key: "deliverables",
              title: "Deliverables",
              url: `/brands/${brandId}/deliverables`,
              icon: CalendarCheck,
            } as BrandLeaf]
          : []),
        { key: "creative-iterations", title: "Creative Iterations", url: `/brands/${brandId}/creative-iterations`, icon: Layers },
        ...(canSeeConceptBoardTypes
          ? [
              { key: "concept-boards", title: "Template Studio", url: `/brands/${brandId}/template-studio/boards`, icon: LayoutTemplate } as BrandLeaf,
            ]
          : []),
        // Ad Launcher — flag gated (no master bypass: the admin setting is
        // authoritative) AND only when the brand has a connected ad account in
        // analytics account management.
        ...(completeAdLauncherSetup
          ? [{ key: "ad-launcher", title: "Ad Launcher", url: `/brands/${brandId}/ad-launcher`, icon: PlaneTakeoff } as BrandLeaf]
          : []),
        ...(shouldShowAdsManager(brandSettings)
          ? [{ key: "meta-ads", title: "Ads Manager", url: `/brands/${brandId}/meta-ads`, icon: Megaphone } as BrandLeaf]
          : []),
        {
          title: "Campaigns",
          icon: Crosshair,
          children: [
            { key: "static-campaigns", title: "Static Campaigns", url: `/brands/${brandId}/campaigns`, icon: Crosshair },
            ...(user?.isMaster
              ? [
                  { key: "ugc-video-campaigns", title: "UGC Video Campaigns", url: `/brands/${brandId}/ugc-campaigns`, icon: Video },
                  { key: "claymation-video-campaigns", title: "Claymation Video Campaigns", url: `/brands/${brandId}/claymation-campaigns`, icon: Shapes },
                  { key: "pixar-video-campaigns", title: "Pixar Video Campaigns", url: `/brands/${brandId}/pixar-campaigns`, icon: Film },
                  { key: "simpsons-video-campaigns", title: "Simpsons Video Campaigns", url: `/brands/${brandId}/simpsons-campaigns`, icon: Film },
                  { key: "dayinlife-video-campaigns", title: "Day in the Life Video Campaigns", url: `/brands/${brandId}/dayinlife-campaigns`, icon: Film },
                ] as BrandLeaf[]
              : []),
          ],
        },
        // Legacy "Landing Pages" entry hidden for everyone for now; routes stay
        // registered and the enableLandingPages setting values are preserved.
        {
          title: "Ad Units",
          icon: LayoutGrid,
          children: [
            { key: "static-ad-units", title: "Static Ad Units", url: `/brands/${brandId}/ad-units`, icon: LayoutGrid },
            { key: "pixar-ad-units", title: "Pixar Ad Units", url: `/brands/${brandId}/pixar-ad-units`, icon: Film },
            { key: "ugc-ad-units", title: "UGC Ad Units", url: `/brands/${brandId}/ugc-templates`, icon: LayoutTemplate },
            ...(user?.isMaster &&
            videoReferenceAvailability?.enabled &&
            !videoReferenceAvailabilityFetching &&
            !videoReferenceAvailabilityError
              ? [{ key: "video-reference-templates", title: "Video Reference Templates", url: `/brands/${brandId}/video-reference-templates`, icon: Film } as BrandLeaf]
              : []),
          ],
        },
      ]
    : [];

  const marketingChannels: BrandNavEntry[] = brandId
    ? [
        ...(user?.isMaster || brandSettings.enableLandingPagesExpress
          ? [{ key: "landing-pages-express", title: "Landing Pages Express", url: `/brands/${brandId}/landing-pages-express`, icon: Rocket } as BrandLeaf]
          : []),
        ...(betaFeatures?.[LP_EXPRESS_2_BETA_FLAG]
          ? [{ key: "landing-pages-express-2", title: "Landing Pages Express 2", url: `/brands/${brandId}/landing-pages-express-2`, icon: Rocket } as BrandLeaf]
          : []),
        ...(user?.isMaster
          ? [{ key: "email-express", title: "Email Express", url: `/brands/${brandId}/email-express`, icon: Mail } as BrandLeaf]
          : []),
        ...(user?.isMaster
          ? [{ key: "email-express-2", title: "Email Express 2.0", url: `/brands/${brandId}/email-express-2`, icon: Mail } as BrandLeaf]
          : []),
      ]
    : [];

  // Creative Performance is intentionally a standalone leaf between Ad Tools
  // and Knowledge Base, not another collapsible section.
  const creativePerformanceItem: BrandLeaf | null =
    brandId && (activeBrand?.settings as any)?.creativePerformance?.enabled
      ? {
          key: "creative-performance",
          title: "Creative Performance",
          url: `/brands/${brandId}/creative-performance`,
          icon: TrendingUp,
        }
      : null;

  const knowledgeBase: BrandNavEntry[] = brandId
    ? [
        { key: "products", title: "Products", url: `/brands/${brandId}/products`, icon: Package },
        { key: "offerings", title: "Offerings", url: `/brands/${brandId}/offers`, icon: Tag },
        { key: "sales-discounts", title: "Sales & Discounts", url: `/brands/${brandId}/sales-discounts`, icon: Percent },
        ...(customerEvidenceAccess.enabled
          ? [{ key: "customer-evidence", title: "Customer Evidence", url: `/brands/${brandId}/customer-evidence`, icon: FileSpreadsheet } as BrandLeaf]
          : []),
        {
          title: "Personas",
          icon: Users,
          children: [
            { key: "all-personas", title: "All Personas", url: `/brands/${brandId}/personas`, icon: Users },
            { key: "persona-ideator", title: "Persona Ideator", url: `/brands/${brandId}/ideator`, icon: Lightbulb },
            { key: "persona-research-lab", title: "Persona Research Lab", url: `/brands/${brandId}/persona-intelligence`, icon: Brain },
            { key: "pain-pillars", title: "Pain Pillars", url: `/brands/${brandId}/pain-pillars`, icon: HeartPulse },
          ],
        },
        { key: "brand-spy", title: "Brand Spy", url: `/brands/${brandId}/brand-spy`, icon: Eye },
        ...(brandSettings.enableLearnings
          ? [{ key: "learnings", title: "Learnings", url: `/brands/${brandId}/learnings`, icon: BookOpen } as BrandLeaf]
          : []),
        { key: "narrative-engine", title: "Narrative Engine", url: `/brands/${brandId}/narratives`, icon: Sparkles },
        // Creative Strategist — flag gated with NO master bypass. The global
        // kill switch closes the entry too, so Hard Stop leaves no dead link.
        ...(creativeStrategistOpen
          ? [{ key: "creative-strategist", title: "Creative Strategist", url: `/brands/${brandId}/creative-strategist`, icon: Compass } as BrandLeaf]
          : []),
      ]
    : [];

  const brandToolSections: BrandToolSection[] = [
    { title: "Image Tools", items: imageTools },
    { title: "Video Tools", items: videoTools },
    { title: "Ad Tools", items: adTools },
    { title: "Marketing Channels", items: marketingChannels },
    { title: "Knowledge Base", items: knowledgeBase },
  ];
  const visibleToolLeaves = [
    ...brandToolSections.slice(0, 4).flatMap((section) =>
      section.items.flatMap((item) => isBrandSubgroup(item) ? item.children : [item]),
    ),
    ...(creativePerformanceItem ? [creativePerformanceItem] : []),
    ...brandToolSections[4].items.flatMap((item) => isBrandSubgroup(item) ? item.children : [item]),
  ];
  const pinnedKeySet = new Set(pinnedKeys);
  const visiblePinnedItems = visibleToolLeaves.filter((item) => pinnedKeySet.has(item.key));

  const handleBrandSwitch = (newBrandId: string) => {
    setLocation(`/brands/${newBrandId}`);
  };

  const renderBrandItem = (brand: Brand) => {
    const content = <>
      <div className="flex items-center gap-2 min-w-0">
        <ImgWithFallback
          src={brand.logoUrl ? optimizedImg(brand.logoUrl, 40, 70) : undefined}
          className="h-5 w-5 rounded object-cover flex-shrink-0"
          fallback={
            <div className="flex h-5 w-5 items-center justify-center rounded bg-primary/10 flex-shrink-0">
              <Building2 className="h-3 w-3 text-primary" />
            </div>
          }
        />
        <span className="text-[13px] truncate">{brand.name}</span>
      </div>
      {brand.id === brandId && (
        <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
      )}
    </>;
    return <DropdownMenuItem
      key={brand.id}
      asChild={internalUrls.enabled}
      onClick={internalUrls.enabled ? undefined : () => handleBrandSwitch(brand.id)}
      className="flex items-center justify-between gap-2 cursor-pointer"
      data-testid={`dropdown-brand-item-${brand.id}`}
    >
      {internalUrls.enabled ? <Link href={`/brands/${brand.id}`}>{content}</Link> : content}
    </DropdownMenuItem>;
  };

  const initials = user
    ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "U"
    : "U";

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <Link href="/" className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary glow-primary">
            <Layers className="h-4 w-4 text-primary-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold tracking-tight" data-testid="text-app-name">Persona OS</span>
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Creative OS</span>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70">Platform</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild data-active={location === item.url || (item.url === "/brands-dashboard" && location.startsWith("/brands-dashboard/"))}>
                    <Link href={item.url} data-testid={`link-${item.title.toLowerCase()}`}>
                      <item.icon className="h-4 w-4" />
                      <span className="text-[13px]">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70">Brand</SidebarGroupLabel>
          <SidebarGroupContent>
            <div className="px-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center justify-between w-full rounded-lg border border-border/50 bg-card px-3 py-2 text-left hover:bg-accent/50 transition-colors cursor-pointer"
                    data-testid="dropdown-brand-selector"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <ImgWithFallback
                        src={activeBrandLogo}
                        className="h-6 w-6 rounded-md object-cover flex-shrink-0"
                        fallback={
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 flex-shrink-0">
                            <Building2 className="h-3.5 w-3.5 text-primary" />
                          </div>
                        }
                      />

                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] font-medium truncate">
                          {brandId ? (brandName || "Loading...") : "Select a brand"}
                        </span>
                        {!brandId && (
                          <span className="text-[10px] text-muted-foreground">Switch workspace</span>
                        )}
                      </div>
                    </div>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 ml-2" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  side="right"
                  className="w-56"
                >
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                    Your Brands
                  </DropdownMenuLabel>
                  {brandsLoading ? (
                    <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Loading brands...
                    </DropdownMenuItem>
                  ) : brands && brands.length > 0 ? (
                    user?.isMaster ? (
                      // Masters see brands grouped by internal client category
                      // (label-only bookkeeping) in collapsible sections; only
                      // collapsed groups are shown until opened. Non-masters keep
                      // the flat list below — the field never reaches their
                      // browser.
                      groupBrandsByClientCategory(brands).map((group) => (
                        <BrandCategoryGroup
                          key={group.key}
                          group={group}
                          renderBrandItem={renderBrandItem}
                        />
                      ))
                    ) : (
                      brands.map((brand) => renderBrandItem(brand))
                    )
                  ) : (
                    <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                      No brands yet
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {user?.isMaster && (
                    <DropdownMenuItem
                      onClick={() => setLocation("/brands/new")}
                      className="cursor-pointer"
                      data-testid="dropdown-brand-create"
                    >
                      <Plus className="h-3.5 w-3.5 mr-2" />
                      <span className="text-[13px]">Create New Brand</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => setLocation("/brands")}
                    className="cursor-pointer"
                    data-testid="dropdown-brand-manage"
                  >
                    <Settings className="h-3.5 w-3.5 mr-2" />
                    <span className="text-[13px]">Manage Brands</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        {brandId && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                {brandName || "Brand"}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {brandPagesBeforeTools.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild data-active={location === item.url}>
                        <Link href={item.url} data-testid={`link-brand-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                          <item.icon className="h-4 w-4" />
                          <span className="text-[13px]">{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                  {creativePerformanceItem && (
                    <BrandLeafItem
                      item={creativePerformanceItem}
                      location={location}
                      pinned={pinnedKeySet.has(creativePerformanceItem.key)}
                      pending={pendingPinKeys.has(creativePerformanceItem.key)}
                      testId="sidebar-creative-performance"
                      onTogglePin={togglePin}
                    />
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {visiblePinnedItems.length > 0 && (
              <SidebarGroup className="pt-0" data-testid="sidebar-pinned">
                <SidebarGroupLabel className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70">
                  Pinned
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visiblePinnedItems.map((item) => (
                      <BrandLeafItem
                        key={`pinned-${item.key}`}
                        item={item}
                        location={location}
                        pinned
                        pending={pendingPinKeys.has(item.key)}
                        pinnedCopy
                        onTogglePin={togglePin}
                      />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {brandToolSections.slice(0, 4).map((section) => (
              <BrandToolSection
                key={section.title}
                section={section}
                location={location}
                manuallyOpen={manuallyOpenSections.has(section.title)}
                manuallyOpenGroups={manuallyOpenGroups}
                onManualOpenChange={(open) => updateManualOpen(setManuallyOpenSections, section.title, open)}
                onGroupManualOpenChange={(groupTitle, open) => updateManualOpen(setManuallyOpenGroups, groupTitle, open)}
                pinnedKeys={pinnedKeySet}
                pendingKeys={pendingPinKeys}
                onTogglePin={togglePin}
              />
            ))}

            <BrandToolSection
              section={brandToolSections[4]}
              location={location}
              manuallyOpen={manuallyOpenSections.has(brandToolSections[4].title)}
              manuallyOpenGroups={manuallyOpenGroups}
              onManualOpenChange={(open) => updateManualOpen(setManuallyOpenSections, brandToolSections[4].title, open)}
              onGroupManualOpenChange={(groupTitle, open) => updateManualOpen(setManuallyOpenGroups, groupTitle, open)}
              pinnedKeys={pinnedKeySet}
              pendingKeys={pendingPinKeys}
              onTogglePin={togglePin}
            />

            <SidebarGroup className="pt-0">
              <SidebarGroupContent>
                <SidebarMenu>
                  {brandPagesAfterTools.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild data-active={location === item.url}>
                        <Link href={item.url} data-testid={`link-brand-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                          <item.icon className="h-4 w-4" />
                          <span className="text-[13px]">{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter className="p-3 space-y-2">
        <CreditBalance brandId={brandId} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center justify-between w-full rounded-lg px-2 py-1.5 hover:bg-accent/50 transition-colors cursor-pointer"
              data-testid="button-user-menu"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar className="h-7 w-7 shrink-0 ring-1 ring-border">
                  <AvatarImage src={user?.profileImageUrl || undefined} />
                  <AvatarFallback className="text-[10px] font-medium bg-primary/10 text-primary">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-medium truncate" data-testid="text-user-name">
                    {user?.firstName || user?.email || "User"}
                  </span>
                  <span className="text-[10px] text-muted-foreground truncate">{user?.email || ""}</span>
                </div>
              </div>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 ml-2" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setLocation("/profile")}
              className="cursor-pointer"
              data-testid="menu-item-profile"
            >
              <User className="h-4 w-4 mr-2" />
              <span className="text-[13px]">Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setLocation("/security")}
              className="cursor-pointer"
              data-testid="menu-item-security"
            >
              <Lock className="h-4 w-4 mr-2" />
              <span className="text-[13px]">Security Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setLocation("/usage")}
              className="cursor-pointer"
              data-testid="menu-item-usage"
            >
              <Receipt className="h-4 w-4 mr-2" />
              <span className="text-[13px]">Usage Log</span>
            </DropdownMenuItem>
            {(user?.isMaster || user?.isSuperuser) && (
              <>
                <DropdownMenuSeparator />
                {/* Story Categories & Video Styles moved into Admin → Meme
                    Settings; superusers see Admin too so they keep access to
                    those libraries (the Admin page limits them to that tab). */}
                <DropdownMenuItem
                  onClick={() => setLocation("/admin")}
                  className="cursor-pointer"
                  data-testid="menu-item-admin"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  <span className="text-[13px]">Admin</span>
                </DropdownMenuItem>
                {user?.isMaster && (
                  <DropdownMenuItem
                    onClick={() => setLocation("/admin/text-agent")}
                    className="cursor-pointer"
                    data-testid="menu-item-admin-text-agent"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    <span className="text-[13px]">Text Agent</span>
                  </DropdownMenuItem>
                )}
                {user?.isMaster && smbAdminAccessVerified && !smbAdminAccessFetching && !smbAdminAccessError && smbAdminAccess?.enabled === true && (
                  <DropdownMenuItem
                    onClick={() => setLocation("/admin/smb")}
                    className="cursor-pointer"
                    data-testid="menu-item-admin-smb"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    <span className="text-[13px]">SMB Test Admin</span>
                  </DropdownMenuItem>
                )}
                {user?.isMaster && (
                  <DropdownMenuItem
                    onClick={() => setLocation("/lp-express-template-studio")}
                    className="cursor-pointer"
                    data-testid="menu-item-lp-express-template-studio"
                  >
                    <LayoutTemplate className="h-4 w-4 mr-2" />
                    <span className="text-[13px]">LP Express Template Studio</span>
                  </DropdownMenuItem>
                )}
                {user?.isMaster && (
                  <>
                    <DropdownMenuItem
                      onClick={() => setLocation("/internal/onboarding")}
                      className="cursor-pointer"
                      data-testid="menu-item-onboarding-guide"
                    >
                      <GraduationCap className="h-4 w-4 mr-2" />
                      <span className="text-[13px]">Onboarding Guide</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setLocation("/internal/mcp-console")}
                      className="cursor-pointer"
                      data-testid="menu-item-mcp-console"
                    >
                      <TerminalSquare className="h-4 w-4 mr-2" />
                      <span className="text-[13px]">MCP Console</span>
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
                window.location.href = "/";
              }}
              className="cursor-pointer text-destructive focus:text-destructive"
              data-testid="menu-item-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span className="text-[13px]">Log Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
